<template>
  <hb-basic-page :title="parseInt(id) === -1 ? $t('threads.new_thread') : thread.title"
                 :loading="loading"
                 :fill-height="loading"
                 color="secondary darken-1"
                 :back-button="{show: true, fallback: 'threads'}"
                 name="edit-thread-view">
    <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2" xl="6" offset-xl="3" class="pb-15" v-scroll="onScroll">

      <v-row v-if="!loading">
        <v-col cols="12">
          <v-card>
            <v-card-subtitle class="text-uppercase primary--text">{{ $t('threads.thread_details') }}</v-card-subtitle>
            <v-card-text>
              <v-form ref="form">
                <v-text-field
                  :disabled="shouldEditingBeDisabled || savingThread"
                  :label="$t('threads.subject')" v-model="thread.title"
                  :rules="[$rules.required]"
                  validate-on-blur
                  type="text"
                />
                <v-textarea
                  :disabled="shouldEditingBeDisabled || savingThread"
                  rows="1" :label="$t('threads.description')" v-model="thread.description" hide-details auto-grow />
              </v-form>
            </v-card-text>
          </v-card>

          <v-card class="mt-3" v-if="parseInt(id) === -1">
            <v-card-subtitle class="text-uppercase primary--text">{{ $t('threads.attachments.title') }}</v-card-subtitle>
            <v-card-text>

              <template v-for="(file, index) in files">
                  <v-card :key="index" style="margin-bottom: 22px;" color="grey lighten-4">
                    <v-col cols="12">
                      <v-row align="center">
                        <v-icon style="padding-left: 8px">attachment</v-icon>
                          <div class="flex-grow-1">
                            <v-text-field
                              v-model="file.name"
                              dense
                              :disabled="shouldEditingBeDisabled || savingThread"
                              hide-details
                              style="padding: 20px 14px 8px 14px;"
                              :label="$t('threads.file_title')"
                              validate-on-blur
                              type="text"
                            ></v-text-field>

                            <v-text-field
                              v-model="file.fileDescription"
                              :label="$t('threads.file_description')"
                              dense
                              :disabled="shouldEditingBeDisabled || savingThread"
                              hide-details
                              class="pa-3"
                              auto
                            ></v-text-field>
                          </div>
                        <v-btn :disabled="shouldEditingBeDisabled || savingThread"
                               icon
                               small
                               style="margin-right: 8px"
                               @click="deleteFile(index)">
                          <v-icon>close</v-icon>
                        </v-btn>
                      </v-row>
                    </v-col>
                  </v-card>
              </template>

              <v-btn :block="$vuetify.breakpoint.xsOnly"
                     :disabled="shouldEditingBeDisabled || savingThread"
                     class="mt-2"
                     color="grey lighten-2"
                     @click="addFiles">
                {{ $t('threads.attachments.button_text') }}
              </v-btn>

              <input type="file" style="display:none" accept="*" :multiple="true" ref="hiddenFilesInput" @change="fileSelected">
            </v-card-text>
          </v-card>
          <v-card class="mt-3">
            <v-card-subtitle class="text-uppercase primary--text">{{ $t('threads.thread_distribution') }}</v-card-subtitle>
            <v-card-text>
              <template v-for="group in distributionGroups">
                <v-checkbox v-model="thread.distribution"
                            color="secondary darken-1"
                            :disabled="shouldEditingBeDisabled || group.disabled || savingThread"
                            :label="group.label" :value="group.value" hide-details :key="group.value" />
              </template>
            </v-card-text>
          </v-card>
          <v-alert :value="showAlert" outlined class="mt-4" type="error">{{ $t('threads.errors.error_validation_message') }}</v-alert>
        </v-col>
      </v-row>

      <v-fade-transition>
        <v-btn
          v-if="!shouldEditingBeDisabled"
          color="secondary darken-1"
          dark
          v-show="ready"
          fixed
          bottom
          right
          rounded
          style="transition: all 0.2s ease-in-out"
          :fab="!scrollingUp"
          large
          :loading="savingThread"
          @click="saveThread">
          <v-icon :left="scrollingUp">mdi mdi-check</v-icon>
          <span v-show="scrollingUp">{{ parseInt(id) === -1 ? $t('threads.publish') : $t('threads.save_changes') }}</span>
        </v-btn>
      </v-fade-transition>

      <v-snackbar v-model="filesUploading"
                  dark
                  top
                  :timeout="-1"
                  max-width="100%"
                  content-class="file-upload-snackbar-content">
        <v-list class="py-0" color="transparent">
          <v-list-item v-for="(file, index) in files"  :key="`${file.fileName}_${index}`" class="px-0">
            <v-list-item-content>
              <v-list-item-title class="caption text-truncate">{{ $t('threads.uploading_file') }} <strong>{{ file.name }}</strong></v-list-item-title>
              <v-list-item-subtitle>
                <v-progress-linear
                  color="secondary"
                  :value="file.uploadingProgress"
                  rounded
                  height="6"
                ></v-progress-linear>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-snackbar>

    </v-col>
  </hb-basic-page>
</template>

<script>
import tenantApi from '@/api/tenant';

export default {
  name: "EditThreadView",
  props: {
    id: {
      type: [String, Number],
      default: -1,
    },
    viewMode: {
      type: [String],
      default: ''
    },
  },
  data() {
    return {
      loading: true,
      ready: false,
      showAlert: false,
      subtleLoading: false,
      scrollingUp: true,
      offsetTop: 0,
      threadTemplate: {
        id: -1,
        title: '',
        description: '',
        distribution: ['tenant_member', 'tenant_chairman', 'tenant_housing_manager', 'tenant_housing_coordinator'],
      },
      thread: {},
      distributionGroups: [
        { value: 'tenant_chairman', label: this.$t('user_management.role.tenant_chairman'), disabled: false },
        { value: 'tenant_member', label: this.$t('user_management.role.tenant_member'), disabled: false },
        { value: 'tenant_substitute_member', label: this.$t('user_management.role.tenant_substitute_member'), disabled: false },
        { value: 'tenant_housing_manager', label: this.$t('user_management.role.tenant_housing_manager'), disabled: true },
        { value: 'tenant_housing_coordinator', label: this.$t('user_management.role.tenant_housing_coordinator'), disabled: true },
      ],
      files: [],
      savingThread: false,
      filesUploading: false,
    }
  },
  methods: {
    onScroll(e) {
      this.scrollingUp = this.offsetTop > e.target.scrollingElement.scrollTop;
      this.offsetTop = e.target.scrollingElement.scrollTop;
    },
    async saveThread() {
      if (!this.$refs.form.validate()) {
        this.showAlert = true;
        return;
      }
      this.savingThread = true;
      try {
        let t;
        if (this.id === -1) {
          t = await tenantApi.createThread(this.thread);
          if (this.files.length > 0) {
            this.filesUploading = true;
            for (const f of this.files) {
              this.$set(f, 'uploadingProgress', 0);
              const handleUploadProgress = (progressEvent) => {
                f.uploadingProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              };
              await tenantApi.uploadThreadAttachment(t.id, f, handleUploadProgress);
            }
            this.filesUploading = false;
          }
          this.savingThread = false;
          this.$router.replace({ name: 'single_thread', params: { id: t.id } });
        } else {
          t = await tenantApi.updateThread(this.thread.id, this.thread);
          this.$router.go(-1);
        }
      } catch (err) {
        this.$handleApiError(err);
      }
      this.filesUploading = false;
      this.savingThread = false;
    },
    async reloadThreadData() {
      try {
        this.thread = await tenantApi.getThread(this.id);
      } catch (err) {
        this.$handleApiError(err);
      }
    },
    addFiles() {
      this.$refs.hiddenFilesInput.click();
    },
    deleteFile(index) {
      this.files.splice(index, 1);
    },
    async fileSelected(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        const asDataUrl = await this.toBase64(f);
        this.files.push({ file: f, dataUrl: asDataUrl, name: f.name, type: f.type, filename: f.name, fileDescription: '' });
      }
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
  },
  async mounted() {
    this.$vuetify.goTo(0, { duration: 0 });

    if (this.$vuetify.breakpoint.xsOnly) {
      setTimeout(() => {
        this.ready = true
      }, 300);
    } else {
      this.ready = true;
    }

    if (parseInt(this.id) > -1) {
      await this.reloadThreadData();
    } else {
      this.thread = Object.assign({}, this.threadTemplate);
    }

    this.loading = false;
  },
  computed: {
    shouldEditingBeDisabled() {
      if (this.$isAdmin || this.thread.id === -1) {
        return false;
      }
      return this.thread.user && (this.thread.user.id !== this.$userInfo.id);
    }
  }
}
</script>

<style lang="scss" scoped>
  .time-slider-label {
    font-size: 1.15rem;
    line-height: 1rem;
    margin-top: -8px;
  }
</style>

<style lang="scss">
  .file-upload-snackbar-content {
    max-width: 100%;
  }
</style>
